import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const { register, handleSubmit, formState: { errors }, setError, clearErrors } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setAuthError(null);
    setErrorType(null);
    clearErrors();
    
    const result = await login(data.email, data.password);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      // Afficher l'erreur selon le type
      setAuthError(result.error);
      setErrorType(result.errorType);
      
      // Définir les erreurs sur les champs appropriés
      if (result.errorType === 'EMAIL_NOT_FOUND') {
        setError('email', { type: 'manual', message: result.error });
      } else if (result.errorType === 'INVALID_PASSWORD') {
        setError('password', { type: 'manual', message: result.error });
      } else if (result.errorType === 'USER_INACTIVE') {
        setError('email', { type: 'manual', message: result.error });
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Connexion</h1>
          <p style={styles.subtitle}>Connectez-vous à votre compte</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
          {authError && (
            <div style={styles.alertError}>
              <span style={styles.alertIcon}>⚠️</span>
              <span>{authError}</span>
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              {...register('email', {
                required: 'L\'email est requis',
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Email invalide',
                },
                onChange: () => {
                  // Effacer l'erreur d'authentification quand l'utilisateur tape
                  if (errorType === 'EMAIL_NOT_FOUND' || errorType === 'USER_INACTIVE') {
                    setAuthError(null);
                    setErrorType(null);
                    clearErrors('email');
                  }
                },
              })}
              style={{
                ...styles.input,
                ...(errors.email || (errorType === 'EMAIL_NOT_FOUND' || errorType === 'USER_INACTIVE') ? styles.inputError : {}),
              }}
              placeholder="votre@email.com"
            />
            {errors.email && (
              <span style={styles.error}>{errors.email.message}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Mot de passe</label>
            <input
              type="password"
              {...register('password', {
                required: 'Le mot de passe est requis',
                minLength: {
                  value: 8,
                  message: 'Le mot de passe doit contenir au moins 8 caractères',
                },
                onChange: () => {
                  // Effacer l'erreur d'authentification quand l'utilisateur tape
                  if (errorType === 'INVALID_PASSWORD') {
                    setAuthError(null);
                    setErrorType(null);
                    clearErrors('password');
                  }
                },
              })}
              style={{
                ...styles.input,
                ...(errors.password || errorType === 'INVALID_PASSWORD' ? styles.inputError : {}),
              }}
              placeholder="••••••••"
            />
            {errors.password && (
              <span style={styles.error}>{errors.password.message}</span>
            )}
          </div>

          <div style={styles.forgotPassword}>
            <Link to="/forgot-password" style={styles.link}>
              Mot de passe oublié ?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={styles.button}
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Pas encore de compte ?{' '}
            <Link to="/register" style={styles.link}>
              S'inscrire
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '1rem',
  },
  card: {
    background: 'white',
    borderRadius: '1rem',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    padding: window.innerWidth <= 480 ? '1.5rem' : '2.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: '0.5rem',
  },
  subtitle: {
    color: 'var(--text-light)',
    fontSize: '0.875rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--text)',
  },
  input: {
    padding: '0.75rem',
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  inputError: {
    borderColor: 'var(--danger)',
  },
  error: {
    color: 'var(--danger)',
    fontSize: '0.75rem',
  },
  alertError: {
    background: '#fee2e2',
    border: '1px solid #fca5a5',
    borderRadius: '0.5rem',
    padding: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#991b1b',
    fontSize: '0.875rem',
  },
  alertIcon: {
    fontSize: '1rem',
  },
  forgotPassword: {
    textAlign: 'right',
  },
  link: {
    color: 'var(--primary)',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  button: {
    padding: '0.75rem',
    background: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  footer: {
    marginTop: '1.5rem',
    textAlign: 'center',
  },
  footerText: {
    color: 'var(--text-light)',
    fontSize: '0.875rem',
  },
};

export default Login;





